import { useState, useEffect, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Coins, SpinnerGap, Receipt, Cube, ChartLineUp, Plus, PencilSimple, Trash,
  DownloadSimple, FileText, X, CheckCircle, ArrowClockwise, Eye,
} from '@phosphor-icons/react';

const authHeader = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const fmtInt = (n) => Number(n || 0).toLocaleString();
const fmtMoney = (n, cur = 'TZS') => `${cur} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtStorage = (gb) => {
  const g = Number(gb || 0);
  return g >= 1 ? `${g.toFixed(2)} GB` : `${(g * 1024).toFixed(2)} MB`;
};
const thisMonth = () => new Date().toISOString().slice(0, 7);

const CARD = 'rounded-2xl border border-[#EDE6D8] bg-white';
const LBL = 'text-[11px] font-semibold text-stone-500 uppercase tracking-wider';
const INP = 'w-full h-10 px-3 rounded-lg border border-[#EDE6D8] text-sm focus:outline-none focus:border-[#0A2540]';
const BTN_PRIMARY = 'h-10 px-4 rounded-xl text-white font-semibold text-sm flex items-center gap-2 disabled:opacity-50';
const GRAD = { background: 'linear-gradient(135deg, #0A2540 0%, #0991d6 100%)' };

export default function BillingPage() {
  const { user } = useAuth();
  const [tab, setTab] = useState('overview');
  const [period, setPeriod] = useState(thisMonth());

  if (user && user.role !== 'super_admin') return <Navigate to="/" replace />;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6" data-testid="billing-page">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h1 className="text-2xl font-bold text-[#0A2540] flex items-center gap-2"><Coins size={26} weight="duotone" />Billing</h1>
          <p className="text-sm text-stone-500 mt-1">Usage-based billing — a plan tier per entity plus overages on API requests &amp; storage.</p>
        </div>
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} data-testid="billing-period"
          className="h-10 px-3 rounded-lg border border-[#EDE6D8] bg-white text-sm text-[#1C1917] focus:outline-none focus:border-[#0A2540]" />
      </div>

      <div className="flex gap-1 mb-5 border-b border-[#EDE6D8]">
        {[
          ['overview', 'Overview', ChartLineUp],
          ['plans', 'Plans', Cube],
          ['invoices', 'Invoices', Receipt],
        ].map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`billing-tab-${k}`}
            className={`px-4 py-2.5 text-sm font-semibold flex items-center gap-1.5 border-b-2 -mb-px transition-colors ${tab === k ? 'border-[#0A2540] text-[#0A2540]' : 'border-transparent text-stone-400 hover:text-stone-600'}`}>
            <Icon size={16} weight={tab === k ? 'fill' : 'regular'} />{label}
          </button>
        ))}
      </div>

      {tab === 'overview' && <OverviewTab period={period} />}
      {tab === 'plans' && <PlansTab />}
      {tab === 'invoices' && <InvoicesTab period={period} />}
    </div>
  );
}

/* ── Overview ──────────────────────────────────────────────────────────────── */

function OverviewTab({ period }) {
  const [data, setData] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [usageFor, setUsageFor] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, p] = await Promise.all([
        axios.get(`${API_URL}/api/billing/entities?period=${period}`, { headers: authHeader() }),
        axios.get(`${API_URL}/api/billing/plans`, { headers: authHeader() }),
      ]);
      setData(e.data);
      setPlans(p.data.plans || []);
    } catch (err) {
      toast.error(formatApiError(err, 'Could not load billing overview'));
    } finally { setLoading(false); }
  }, [period]);

  useEffect(() => { load(); }, [load]);

  const assign = async (entityId, planId) => {
    try {
      await axios.put(`${API_URL}/api/billing/entities/${entityId}/plan`, { plan_id: planId }, { headers: authHeader() });
      toast.success('Plan assigned');
      load();
    } catch (err) { toast.error(formatApiError(err, 'Assign failed')); }
  };

  const generateAll = async () => {
    if (!window.confirm(`Generate invoices for ALL entities for ${period}? This recomputes usage and creates/updates draft invoices.`)) return;
    setGenerating(true);
    try {
      const r = await axios.post(`${API_URL}/api/billing/invoices/generate`, { period }, { headers: authHeader() });
      toast.success(`${r.data.generated} invoice(s) generated for ${period}`);
    } catch (err) { toast.error(formatApiError(err, 'Generation failed')); }
    finally { setGenerating(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0A2540]" /></div>;

  const rows = data?.entities || [];
  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm text-stone-500">Month-to-date usage for <b className="text-[#0A2540]">{data?.period}</b>. SMS &amp; all modules are included — never billed separately.</p>
        <div className="flex gap-2">
          <button onClick={load} className="h-9 px-3 rounded-lg border border-[#EDE6D8] text-sm font-medium text-[#0A2540] hover:bg-stone-50 flex items-center gap-1.5" data-testid="overview-refresh"><ArrowClockwise size={15} />Refresh</button>
          <button onClick={generateAll} disabled={generating} className={BTN_PRIMARY} style={GRAD} data-testid="generate-all-invoices">
            {generating ? <SpinnerGap size={16} className="animate-spin" /> : <Receipt size={16} />}Generate invoices
          </button>
        </div>
      </div>

      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FCFAF6]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-[#EDE6D8]">
                <th className="py-3 px-4 font-semibold">Entity</th>
                <th className="py-3 px-4 font-semibold">Plan</th>
                <th className="py-3 px-4 font-semibold text-right">Requests (inbound + upstream)</th>
                <th className="py-3 px-4 font-semibold text-right">Storage avg</th>
                <th className="py-3 px-4 font-semibold text-right">Est. charge</th>
                <th className="py-3 px-4 font-semibold text-right"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e) => (
                <tr key={e.entity_id} className="border-b border-[#F4F0E8] last:border-0 hover:bg-[#FCFAF6]/60" data-testid={`billing-entity-row-${e.entity_id}`}>
                  <td className="py-3 px-4">
                    <div className="font-semibold text-[#0A2540]">{e.entity_name}</div>
                    <div className="text-[11px] text-stone-400">ID {e.entity_id}</div>
                  </td>
                  <td className="py-3 px-4">
                    <select value={e.plan_id || ''} onChange={(ev) => assign(e.entity_id, ev.target.value)} data-testid={`plan-select-${e.entity_id}`}
                      className="h-9 px-2 rounded-lg border border-[#EDE6D8] text-sm bg-white focus:outline-none focus:border-[#0A2540]">
                      {plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {!e.assigned && <span className="ml-2 text-[10px] text-stone-400 uppercase">default</span>}
                  </td>
                  <td className="py-3 px-4 text-right">
                    <div className="font-semibold text-[#0A2540]">{fmtInt(e.usage.total_requests)}</div>
                    <div className="text-[11px] text-stone-400">{fmtInt(e.usage.api_requests)} in · {fmtInt(e.usage.mw_requests)} up</div>
                  </td>
                  <td className="py-3 px-4 text-right text-[#1C1917]">{fmtStorage(e.usage.avg_storage_gb)}</td>
                  <td className="py-3 px-4 text-right font-bold text-[#0A2540]">{fmtMoney(e.estimated_total, e.currency)}</td>
                  <td className="py-3 px-4 text-right">
                    <button onClick={() => setUsageFor(e)} className="h-8 px-2.5 rounded-lg border border-[#EDE6D8] text-xs font-medium text-[#0A2540] hover:bg-white flex items-center gap-1 ml-auto" data-testid={`view-usage-${e.entity_id}`}><Eye size={14} />Usage</button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-400 text-sm">No entities found.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {usageFor && <UsageModal entity={usageFor} period={period} onClose={() => setUsageFor(null)} />}
    </div>
  );
}

function UsageModal({ entity, period, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API_URL}/api/billing/entities/${entity.entity_id}/usage?period=${period}`, { headers: authHeader() })
      .then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [entity.entity_id, period]);

  const cur = entity.currency || 'TZS';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="usage-modal">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 p-5 border-b border-[#EDE6D8]">
          <div className="min-w-0">
            <h3 className="font-bold text-[#0A2540] truncate">{entity.entity_name} — usage</h3>
            <p className="text-xs text-stone-500">{period} · plan {data?.plan?.name || entity.plan_name}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid="usage-close"><X size={16} /></button>
        </div>
        <div className="overflow-auto p-5">
          {loading ? <div className="flex justify-center py-12"><SpinnerGap size={24} className="animate-spin text-[#0A2540]" /></div> : (
            <>
              <div className="grid grid-cols-3 gap-3 mb-5">
                <Metric label="Inbound requests" value={fmtInt(data?.usage?.api_requests)} />
                <Metric label="Upstream calls" value={fmtInt(data?.usage?.mw_requests)} />
                <Metric label="Storage (avg)" value={fmtStorage(data?.usage?.avg_storage_gb)} />
              </div>
              <div className="mb-5">
                <div className={LBL + ' mb-2'}>Charge breakdown</div>
                <div className="rounded-xl border border-[#EDE6D8] divide-y divide-[#F4F0E8]">
                  {(data?.charges?.line_items || []).map((li, i) => (
                    <div key={i} className="flex items-start justify-between gap-3 px-3 py-2">
                      <div><div className="text-sm text-[#1C1917]">{li.label}</div><div className="text-[11px] text-stone-400">{li.detail}</div></div>
                      <div className="text-sm font-semibold text-[#0A2540] whitespace-nowrap">{fmtMoney(li.amount, cur)}</div>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-3 py-2.5 bg-[#FCFAF6]">
                    <div className="text-sm font-bold text-[#0A2540]">Estimated total</div>
                    <div className="text-sm font-bold text-[#0A2540]">{fmtMoney(data?.charges?.total, cur)}</div>
                  </div>
                </div>
              </div>
              <div className={LBL + ' mb-2'}>Daily usage</div>
              {(data?.daily || []).length === 0 ? <p className="text-sm text-stone-400">No usage recorded yet this period.</p> : (
                <div className="max-h-48 overflow-y-auto rounded-xl border border-[#EDE6D8]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-[#FCFAF6]"><tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-[#EDE6D8]">
                      <th className="py-2 px-3 font-semibold">Date</th><th className="py-2 px-3 font-semibold text-right">Inbound</th><th className="py-2 px-3 font-semibold text-right">Upstream</th><th className="py-2 px-3 font-semibold text-right">Storage MB</th>
                    </tr></thead>
                    <tbody>
                      {data.daily.map((d) => (
                        <tr key={d.date} className="border-b border-[#F4F0E8] last:border-0">
                          <td className="py-1.5 px-3 text-stone-600">{d.date}</td>
                          <td className="py-1.5 px-3 text-right">{fmtInt(d.api_requests)}</td>
                          <td className="py-1.5 px-3 text-right">{fmtInt(d.mw_requests)}</td>
                          <td className="py-1.5 px-3 text-right">{(d.storage_mb || 0).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const Metric = ({ label, value }) => (
  <div className="rounded-xl border border-[#EDE6D8] bg-[#FCFAF6] px-3 py-3">
    <div className="text-lg font-bold text-[#0A2540]">{value}</div>
    <div className="text-[10px] uppercase tracking-wider text-stone-400">{label}</div>
  </div>
);

/* ── Plans ─────────────────────────────────────────────────────────────────── */

const BLANK_PLAN = {
  name: '', base_fee: 0, currency: 'TZS', included_requests: 0, overage_per_1k_requests: 0,
  included_storage_gb: 0, overage_per_gb: 0, tier_order: 0, cycle: 'monthly', is_default: false,
};

function PlansTab() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/billing/plans`, { headers: authHeader() });
      setPlans(r.data.plans || []);
    } catch (err) { toast.error(formatApiError(err, 'Could not load plans')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const del = async (p) => {
    if (!window.confirm(`Delete plan "${p.name}"?`)) return;
    try { await axios.delete(`${API_URL}/api/billing/plans/${p.id}`, { headers: authHeader() }); toast.success('Plan deleted'); load(); }
    catch (err) { toast.error(formatApiError(err, 'Delete failed')); }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0A2540]" /></div>;

  return (
    <div>
      <div className="flex justify-end mb-3">
        <button onClick={() => setEditing({ ...BLANK_PLAN, tier_order: plans.length + 1 })} className={BTN_PRIMARY} style={GRAD} data-testid="add-plan"><Plus size={16} />Add plan</button>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {plans.map((p) => (
          <div key={p.id} className={`${CARD} p-5`} data-testid={`plan-card-${p.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <div className="font-bold text-[#0A2540] text-lg flex items-center gap-2">{p.name}{p.is_default && <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-[#2F6D4B] bg-[#DCEBE2] px-2 py-0.5 rounded-full"><CheckCircle size={11} weight="fill" />Default</span>}</div>
                <div className="text-2xl font-bold text-[#0A2540] mt-1">{fmtMoney(p.base_fee, p.currency)}<span className="text-xs font-normal text-stone-400">/mo</span></div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setEditing(p)} className="w-8 h-8 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid={`edit-plan-${p.id}`}><PencilSimple size={15} /></button>
                <button onClick={() => del(p)} className="w-8 h-8 rounded-lg border border-[#EDE6D8] text-[#B4453A] flex items-center justify-center hover:bg-red-50" data-testid={`del-plan-${p.id}`}><Trash size={15} /></button>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm">
              <Row label="Included requests" value={fmtInt(p.included_requests)} />
              <Row label="Req. overage" value={`${fmtMoney(p.overage_per_1k_requests, p.currency)} / 1k`} />
              <Row label="Included storage" value={`${p.included_storage_gb} GB`} />
              <Row label="Storage overage" value={`${fmtMoney(p.overage_per_gb, p.currency)} / GB`} />
            </div>
          </div>
        ))}
      </div>
      {editing && <PlanModal plan={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between"><span className="text-stone-500">{label}</span><span className="font-semibold text-[#0A2540]">{value}</span></div>
);

function PlanModal({ plan, onClose, onSaved }) {
  const [form, setForm] = useState({ ...BLANK_PLAN, ...plan });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const num = (k, v) => set(k, v === '' ? 0 : Number(v));
  const isNew = !plan.id;

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    const payload = {
      name: form.name, base_fee: Number(form.base_fee), currency: form.currency || 'TZS',
      included_requests: Number(form.included_requests), overage_per_1k_requests: Number(form.overage_per_1k_requests),
      included_storage_gb: Number(form.included_storage_gb), overage_per_gb: Number(form.overage_per_gb),
      tier_order: Number(form.tier_order), cycle: 'monthly', is_default: !!form.is_default,
    };
    try {
      if (isNew) await axios.post(`${API_URL}/api/billing/plans`, payload, { headers: authHeader() });
      else await axios.put(`${API_URL}/api/billing/plans/${plan.id}`, payload, { headers: authHeader() });
      toast.success(isNew ? 'Plan created' : 'Plan updated');
      onSaved();
    } catch (err) { toast.error(formatApiError(err, 'Save failed')); }
    finally { setSaving(false); }
  };

  // Render helper (called as a function, NOT as <F/>) so inputs aren't remounted on each keystroke.
  const F = ({ label, k, step }) => (
    <div><label className={LBL}>{label}</label><input type="number" step={step || 1} className={INP} value={form[k]} onChange={(e) => num(k, e.target.value)} data-testid={`plan-field-${k}`} /></div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="plan-modal">
      <div className="bg-white rounded-2xl w-full max-w-lg max-h-[88vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#EDE6D8]">
          <h3 className="font-bold text-[#0A2540]">{isNew ? 'New plan' : `Edit ${plan.name}`}</h3>
          <button onClick={onClose} className="w-9 h-9 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid="plan-modal-close"><X size={16} /></button>
        </div>
        <div className="overflow-auto p-5 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className={LBL}>Plan name</label><input className={INP} value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="plan-field-name" /></div>
            <div><label className={LBL}>Currency</label><input className={INP} value={form.currency} onChange={(e) => set('currency', e.target.value)} data-testid="plan-field-currency" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F({ label: "Base fee / mo", k: "base_fee" })}
            {F({ label: "Tier order", k: "tier_order" })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F({ label: "Included requests", k: "included_requests" })}
            {F({ label: "Overage / 1k requests", k: "overage_per_1k_requests", step: "0.01" })}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {F({ label: "Included storage (GB)", k: "included_storage_gb", step: "0.1" })}
            {F({ label: "Overage / GB", k: "overage_per_gb", step: "0.01" })}
          </div>
          <label className="flex items-center gap-2 text-sm text-[#1C1917] pt-1">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => set('is_default', e.target.checked)} className="w-4 h-4 accent-[#0A2540]" data-testid="plan-field-default" />
            Default plan (used by entities with no explicit assignment)
          </label>
        </div>
        <div className="flex justify-end gap-2 p-5 border-t border-[#EDE6D8]">
          <button onClick={onClose} className="h-10 px-4 rounded-xl border border-[#EDE6D8] text-sm font-medium text-[#0A2540] hover:bg-stone-50">Cancel</button>
          <button onClick={save} disabled={saving} className={BTN_PRIMARY} style={GRAD} data-testid="plan-save">{saving ? <SpinnerGap size={16} className="animate-spin" /> : null}Save plan</button>
        </div>
      </div>
    </div>
  );
}

/* ── Invoices ──────────────────────────────────────────────────────────────── */

function InvoicesTab({ period }) {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/billing/invoices?period=${period}`, { headers: authHeader() });
      setInvoices(r.data.invoices || []);
    } catch (err) { toast.error(formatApiError(err, 'Could not load invoices')); }
    finally { setLoading(false); }
  }, [period]);
  useEffect(() => { load(); }, [load]);

  const download = async (inv, kind) => {
    try {
      const res = await axios.get(`${API_URL}/api/billing/invoices/${inv.id}/export.${kind}`, { headers: authHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `invoice-${(inv.entity_name || 'entity').replace(/\s+/g, '-').toLowerCase()}-${inv.period_key}.${kind}`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="flex justify-center py-20"><SpinnerGap size={28} className="animate-spin text-[#0A2540]" /></div>;

  return (
    <div>
      <p className="text-sm text-stone-500 mb-3">Invoices for <b className="text-[#0A2540]">{period}</b>. Generate them from the Overview tab.</p>
      <div className={`${CARD} overflow-hidden`}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#FCFAF6]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-stone-400 border-b border-[#EDE6D8]">
                <th className="py-3 px-4 font-semibold">Entity</th>
                <th className="py-3 px-4 font-semibold">Plan</th>
                <th className="py-3 px-4 font-semibold">Period</th>
                <th className="py-3 px-4 font-semibold text-right">Total</th>
                <th className="py-3 px-4 font-semibold">Status</th>
                <th className="py-3 px-4 font-semibold text-right">Export</th>
              </tr>
            </thead>
            <tbody>
              {invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#F4F0E8] last:border-0 hover:bg-[#FCFAF6]/60" data-testid={`invoice-row-${inv.id}`}>
                  <td className="py-3 px-4 font-semibold text-[#0A2540]">{inv.entity_name}</td>
                  <td className="py-3 px-4 text-stone-600">{inv.plan?.name || '—'}</td>
                  <td className="py-3 px-4 text-stone-600">{inv.period_key}</td>
                  <td className="py-3 px-4 text-right font-bold text-[#0A2540]">{fmtMoney(inv.total, inv.currency)}</td>
                  <td className="py-3 px-4"><span className="text-[10px] uppercase tracking-wider text-[#8A6D1F] bg-[#F6EEDC] px-2 py-0.5 rounded-full font-semibold">{inv.status}</span></td>
                  <td className="py-3 px-4">
                    <div className="flex gap-1.5 justify-end">
                      <button onClick={() => setViewing(inv)} className="h-8 px-2.5 rounded-lg border border-[#EDE6D8] text-xs font-medium text-[#0A2540] hover:bg-white flex items-center gap-1" data-testid={`invoice-view-${inv.id}`}><Eye size={14} />View</button>
                      <button onClick={() => download(inv, 'xlsx')} className="h-8 px-2.5 rounded-lg border border-[#EDE6D8] text-xs font-medium text-[#0A2540] hover:bg-white flex items-center gap-1" data-testid={`invoice-xlsx-${inv.id}`}><DownloadSimple size={14} />Excel</button>
                      <button onClick={() => download(inv, 'pdf')} className="h-8 px-2.5 rounded-lg border border-[#EDE6D8] text-xs font-medium text-[#0A2540] hover:bg-white flex items-center gap-1" data-testid={`invoice-pdf-${inv.id}`}><FileText size={14} />PDF</button>
                    </div>
                  </td>
                </tr>
              ))}
              {invoices.length === 0 && <tr><td colSpan={6} className="py-10 text-center text-stone-400 text-sm" data-testid="invoices-empty">No invoices for this period yet. Generate them from the Overview tab.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
      {viewing && <InvoiceModal invoice={viewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

function InvoiceModal({ invoice, onClose }) {
  const cur = invoice.currency || 'TZS';
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose} data-testid="invoice-modal">
      <div className="bg-white rounded-2xl w-full max-w-xl max-h-[85vh] flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[#EDE6D8]">
          <div>
            <h3 className="font-bold text-[#0A2540]">{invoice.entity_name}</h3>
            <p className="text-xs text-stone-500">{invoice.period_start} → {invoice.period_end} · {invoice.plan?.name}</p>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-lg border border-[#EDE6D8] flex items-center justify-center hover:bg-stone-50" data-testid="invoice-modal-close"><X size={16} /></button>
        </div>
        <div className="overflow-auto p-5">
          <div className="rounded-xl border border-[#EDE6D8] divide-y divide-[#F4F0E8]">
            {(invoice.line_items || []).map((li, i) => (
              <div key={i} className="flex items-start justify-between gap-3 px-4 py-3">
                <div><div className="text-sm text-[#1C1917] font-medium">{li.label}</div><div className="text-[11px] text-stone-400">{li.detail}</div></div>
                <div className="text-sm font-semibold text-[#0A2540] whitespace-nowrap">{fmtMoney(li.amount, cur)}</div>
              </div>
            ))}
            <div className="flex items-center justify-between px-4 py-3.5 bg-[#FCFAF6]">
              <div className="text-base font-bold text-[#0A2540]">Total</div>
              <div className="text-base font-bold text-[#0A2540]">{fmtMoney(invoice.total, cur)}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
